import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { useTheme } from '../contexts/ThemeContext';
import api from '../lib/api';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import PremiumLock from '../components/PremiumLock';
import { Card, CardContent } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Flame, Trophy, Target, TrendingUp, Calendar as CalendarIcon, Zap, Plus, Minus, ChevronDown, Users } from 'lucide-react';
import { format, isSameDay, parseISO, startOfMonth, endOfMonth, differenceInDays, subDays, startOfDay } from 'date-fns';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

export default function GoalCalendar() {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [goals, setGoals] = useState([]);
  const [sharedGoals, setSharedGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showActivity, setShowActivity] = useState(true);
  const [showSharedGoals, setShowSharedGoals] = useState(true);

  const fetchData = async () => {
    try {
      const [goalsRes, sharedRes] = await Promise.all([
        api.get('/api/goals'),
        api.get('/api/shared-goals').catch(() => ({ data: [] }))
      ]);
      setGoals(goalsRes.data);
      setSharedGoals(sharedRes.data.filter(g => g.status === 'ACTIVE'));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.is_premium) {
      fetchData();
    }
  }, [user?.is_premium]);

  // Handle updating count for a specific goal on a specific date
  const handleUpdateCount = async (goalId, dateStr, newValue) => {
    try {
      await api.post(`/api/goals/${goalId}/log`, { 
        date: dateStr, 
        value: Math.max(0, newValue) 
      });
      toast.success(newValue > 0 ? "Count updated!" : "Entry removed");
      fetchData();
    } catch (e) {
      toast.error("Failed to update count");
    }
  };

  // Get all dates that have logs - ONLY from daily_logs (rich logs with value > 0)
  const getDatesWithLogs = () => {
    const logDates = new Set();
    goals.forEach(goal => {
      // Only use daily_logs - ignore legacy logs array as it may contain invalid data
      const sourceLogs = goal.daily_logs || [];
      sourceLogs.forEach(log => {
        // Only count logs with value > 0
        if (log && typeof log === 'object' && log.date && log.value > 0) {
          logDates.add(log.date.split('T')[0]);
        }
      });
    });
    return logDates;
  };

  // Calculate streaks
  const calculateStreaks = () => {
    const logDates = getDatesWithLogs();
    const sortedDates = Array.from(logDates).sort().reverse();
    
    if (sortedDates.length === 0) {
      return { currentStreak: 0, longestStreak: 0, totalDays: 0 };
    }

    const today = startOfDay(new Date());
    let currentStreak = 0;
    let longestStreak = 0;
    let tempStreak = 0;
    let lastDate = null;
    
    // Sort dates ascending for streak calculation
    const ascendingDates = [...sortedDates].reverse();
    
    ascendingDates.forEach((dateStr, index) => {
      const currentDate = parseISO(dateStr);
      
      if (lastDate === null) {
        tempStreak = 1;
      } else {
        const daysDiff = differenceInDays(currentDate, lastDate);
        if (daysDiff === 1) {
          tempStreak++;
        } else {
          tempStreak = 1;
        }
      }
      
      longestStreak = Math.max(longestStreak, tempStreak);
      lastDate = currentDate;
    });

    // Calculate current streak (from today backwards)
    const todayStr = format(today, 'yyyy-MM-dd');
    const yesterdayStr = format(subDays(today, 1), 'yyyy-MM-dd');
    
    if (logDates.has(todayStr) || logDates.has(yesterdayStr)) {
      let checkDate = logDates.has(todayStr) ? today : subDays(today, 1);
      currentStreak = 0;
      
      while (logDates.has(format(checkDate, 'yyyy-MM-dd'))) {
        currentStreak++;
        checkDate = subDays(checkDate, 1);
      }
    }

    return { 
      currentStreak, 
      longestStreak, 
      totalDays: logDates.size 
    };
  };

  const streakStats = calculateStreaks();
  const logDatesSet = getDatesWithLogs();

  // Aggregate logs for calendar modifiers
  const getModifiers = () => {
      const modifiers = {
          success: [],
          failed: [],
      };
      
      goals.forEach(goal => {
          // Only use daily_logs - ignore legacy logs
          const sourceLogs = goal.daily_logs || [];
          
          sourceLogs.forEach(log => {
              // Only count logs with value > 0
              if (log && typeof log === 'object' && log.date && log.value > 0) {
                  modifiers.success.push(parseISO(log.date));
              }
          });
          
          if (goal.status === 'FAILED') {
              modifiers.failed.push(parseISO(goal.end_date));
          }
      });
      return modifiers;
  };

  const getSelectedGoals = (day) => {
      if (!day) return [];
      const logs = [];
      goals.forEach(goal => {
          // Only use daily_logs - ignore legacy logs
          const sourceLogs = goal.daily_logs || [];
          
          const logEntry = sourceLogs.find(log => {
              if (log && typeof log === 'object' && log.date) {
                  return isSameDay(parseISO(log.date), day);
              }
              return false;
          });

          if (logEntry && logEntry.value > 0) {
              logs.push({ ...goal, type: 'log', value: logEntry.value, tracking_type: goal.tracking_type });
          }
          
          if (isSameDay(parseISO(goal.end_date), day)) {
              logs.push({ ...goal, type: 'end', tracking_type: goal.tracking_type });
          }
      });
      return logs;
  }

  const selectedDayLogs = getSelectedGoals(date);

  // Monthly summary calculation
  const getMonthlyStats = (monthDate) => {
      const start = startOfMonth(monthDate);
      const end = endOfMonth(monthDate);
      
      let totalLogs = 0;
      let goalsCompleted = 0;
      let goalsFailed = 0;

      goals.forEach(goal => {
          // Only use daily_logs - ignore legacy logs
          const sourceLogs = goal.daily_logs || [];
          
          const monthLogs = sourceLogs.filter(log => {
              if (log && typeof log === 'object' && log.date && log.value > 0) {
                  const d = parseISO(log.date);
                  return d >= start && d <= end;
              }
              return false;
          });
          totalLogs += monthLogs.length;

          const endDate = parseISO(goal.end_date);
          if (endDate >= start && endDate <= end) {
              if (goal.status === 'COMPLETED') goalsCompleted++;
              if (goal.status === 'FAILED') goalsFailed++;
          }
      });

      return { totalLogs, goalsCompleted, goalsFailed };
  };

  const monthStats = getMonthlyStats(currentMonth);

  // Custom day content renderer
  const renderDay = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    const hasLog = logDatesSet.has(dateStr);
    const isToday = isSameDay(day, new Date());
    
    return (
      <div className={`
        relative w-full h-full flex items-center justify-center
        ${hasLog ? 'font-bold text-emerald-400' : 'text-zinc-300'}
      `}>
        {day.getDate()}
        {hasLog && (
          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.6)]" />
        )}
      </div>
    );
  };

  // Premium check after all hooks
  if (!user?.is_premium) {
    return (
      <div className={cn(
        "min-h-screen transition-colors duration-300",
        isDark ? "bg-[#050505]" : "bg-[#FAF9F5]"
      )}>
        <Navbar />
        <div className="max-w-6xl mx-auto p-4 md:p-6">
          <PremiumLock featureName="Performance Calendar" />
        </div>
      </div>
    );
  }

  return (
    <div className={cn(
      "min-h-screen transition-colors duration-300",
      isDark ? "bg-[#050505]" : "bg-[#FAF9F5]"
    )}>
      <Navbar />
      <div className="max-w-6xl mx-auto space-y-4 p-4 md:p-6">
          {/* Clean header + inline stat chips */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <div>
              <p className="sl-section-tag mb-2">PERFORMANCE CALENDAR</p>
              <h1 className="text-3xl sm:text-4xl font-semibold tracking-[-0.03em] text-[#1A1A1A] leading-tight">
                Your rhythm.
              </h1>
              <p className="mt-2 text-sm text-[#5F5F5F]">
                Every day you showed up. Every day you didn't.
              </p>
            </div>

            {/* Unified muted stat chips */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="sl-chip-coral">
                <Flame className="w-3.5 h-3.5" />
                <span className="font-semibold">{streakStats.currentStreak}</span>
                <span className="opacity-70">streak</span>
              </div>
              <div className="sl-chip">
                <Trophy className="w-3.5 h-3.5" />
                <span className="font-semibold">{streakStats.longestStreak}</span>
                <span className="opacity-70">best</span>
              </div>
              <div className="sl-chip">
                <Target className="w-3.5 h-3.5" />
                <span className="font-semibold">{streakStats.totalDays}</span>
                <span className="opacity-70">days</span>
              </div>
              <div className="hidden sm:inline-flex sl-chip">
                <TrendingUp className="w-3.5 h-3.5" />
                <span className="font-semibold">{monthStats.totalLogs}</span>
                <span className="opacity-70">this month</span>
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-[1fr_280px] gap-4">
              {/* Calendar Card - Compact */}
              <Card className={cn("p-4 shadow-sm", isDark ? "glass-card" : "bg-white border-[#EEEAE0]")}>
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    onMonthChange={setCurrentMonth}
                    className="rounded-md w-full max-w-full"
                    classNames={{
                        month: "space-y-3 w-full",
                        caption: "flex justify-center pt-1 relative items-center",
                        caption_label: cn("text-base font-semibold", isDark ? "text-white" : "text-[#1A1A1A]"),
                        nav: "space-x-1 flex items-center",
                        nav_button: cn("h-7 w-7 rounded-lg p-0 opacity-70 hover:opacity-100 transition-all", isDark ? "bg-zinc-800 hover:bg-zinc-700" : "bg-[#F4F3EE] hover:bg-violet-200"),
                        nav_button_previous: "absolute left-1",
                        nav_button_next: "absolute right-1",
                        table: "w-full border-collapse",
                        head_row: "flex w-full",
                        head_cell: cn("rounded-md w-full font-medium text-xs uppercase tracking-wider", isDark ? "text-zinc-400" : "text-[#9E8778]"),
                        row: "flex w-full mt-1",
                        cell: "h-10 w-full text-center text-sm p-0 relative [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
                        day: cn("h-10 w-full p-0 font-medium aria-selected:opacity-100 rounded-lg transition-all cursor-pointer text-sm", isDark ? "hover:bg-zinc-800 text-zinc-300" : "hover:bg-[#F4F3EE] text-[#5F5F5F]"),
                        day_selected: cn("bg-[#F4F3EE]0 text-white hover:bg-violet-600 hover:text-white focus:bg-[#F4F3EE]0 focus:text-white ring-2 ring-violet-500 ring-offset-1", isDark ? "ring-offset-zinc-900" : "ring-offset-white"),
                        day_today: cn("font-bold ring-1", isDark ? "bg-zinc-800 ring-zinc-600" : "bg-[#F4F3EE] ring-violet-300"),
                        day_outside: cn("opacity-50", isDark ? "text-zinc-600" : "text-slate-300"),
                        day_disabled: cn("opacity-30", isDark ? "text-zinc-600" : "text-slate-300"),
                    }}
                    modifiers={getModifiers()}
                    modifiersClassNames={{
                        success: isDark ? "!text-emerald-400 !font-bold bg-emerald-900/20" : "!text-emerald-600 !font-bold bg-emerald-100",
                        failed: isDark ? "!text-red-400 !font-bold bg-red-900/20" : "!text-red-600 !font-bold bg-red-100"
                    }}
                  />
                  
                  {/* Monthly Stats Row - Below Calendar */}
                  <div className={cn("flex items-center justify-center gap-3 mt-4 pt-4 border-t", isDark ? "border-zinc-800" : "border-[#EEEAE0]")}>
                      <div className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-lg border", isDark ? "bg-blue-500/10 border-blue-500/30" : "bg-blue-50 border-blue-200")}>
                          <span className={cn("text-lg font-bold", isDark ? "text-blue-400" : "text-blue-600")}>{monthStats.totalLogs}</span>
                          <span className={cn("text-xs uppercase font-medium", isDark ? "text-blue-400/70" : "text-blue-500")}>logs</span>
                      </div>
                      <div className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-lg border", isDark ? "bg-emerald-500/10 border-emerald-500/30" : "bg-emerald-50 border-[#EEEAE0]")}>
                          <span className={cn("text-lg font-bold", isDark ? "text-emerald-400" : "text-emerald-600")}>{monthStats.goalsCompleted}</span>
                          <span className={cn("text-xs uppercase font-medium", isDark ? "text-emerald-400/70" : "text-emerald-500")}>won</span>
                      </div>
                      <div className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-lg border", isDark ? "bg-[#FDF0EC]0/10 border-[#F5D5CB]" : "bg-[#FDF0EC] border-[#F5E9DE]")} title="Failed goals with pledges charged">
                          <span className={cn("text-lg font-bold", isDark ? "text-coral" : "text-coral")}>{monthStats.goalsFailed}</span>
                          <span className={cn("text-xs uppercase font-medium", isDark ? "text-coral/70" : "text-coral")}>lost</span>
                      </div>
                  </div>
              </Card>

              {/* Activity Panel - Collapsible on Mobile */}
              <div className="space-y-3">
                <button 
                  onClick={() => setShowActivity(!showActivity)}
                  className={cn(
                    "lg:hidden w-full flex items-center justify-between px-3 py-2 rounded-lg border",
                    isDark ? "bg-zinc-900/50 border-zinc-800" : "bg-white border-[#EEEAE0]"
                  )}
                >
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-violet-400" />
                    <span className={cn("text-sm font-medium", isDark ? "text-zinc-300" : "text-[#5F5F5F]")}>Activity for {date ? format(date, 'MMM d') : 'Selected Day'}</span>
                  </div>
                  <ChevronDown className={cn("w-4 h-4 transition-transform", isDark ? "text-zinc-400" : "text-[#9E8778]", !showActivity && "-rotate-90")} />
                </button>
                
                <Card className={cn(
                  isDark ? "glass-card card-shine" : "bg-white border-[#EEEAE0] shadow-sm",
                  !showActivity && "lg:block hidden"
                )}>
                  <div className={cn("p-3 border-b", isDark ? "border-zinc-800" : "border-[#EEEAE0]")}>
                    <div className={cn("flex items-center gap-2 text-sm font-medium", isDark ? "text-white" : "text-[#1A1A1A]")}>
                      <CalendarIcon className="w-4 h-4 text-violet-400" />
                      {date ? format(date, 'MMMM d') : 'Select a day'}
                    </div>
                  </div>
                  <CardContent className="p-3">
                      {selectedDayLogs.length === 0 ? (
                          <div className={cn(
                            "text-center py-6 rounded-lg border border-dashed",
                            isDark ? "text-zinc-400 bg-zinc-800/50 border-zinc-700" : "text-[#9E8778] bg-slate-50 border-[#E5E2DA]"
                          )}>
                              <CalendarIcon className={cn("w-8 h-8 mx-auto mb-2", isDark ? "text-zinc-600" : "text-slate-300")} />
                              <p className="text-sm font-medium">No activity</p>
                              <p className={cn("text-xs mt-0.5", isDark ? "text-zinc-500" : "text-[#9E8778]")}>Select a day to see details</p>
                          </div>
                      ) : (
                          <ScrollArea className="h-[280px]">
                              <div className="space-y-2">
                                  {selectedDayLogs.map((item, i) => {
                                      const isCountType = item.tracking_type === 'count';
                                      const dateStr = date ? format(date, 'yyyy-MM-dd') : '';
                                      
                                      return (
                                          <div key={i} className={cn(
                                            "p-3 rounded-lg border",
                                            isDark ? "border-zinc-800 bg-zinc-800/30" : "border-[#EEEAE0] bg-[#F4F3EE]/30"
                                          )}>
                                              <div className="flex items-start gap-2">
                                                  <div className={cn(
                                                    "mt-1 w-2 h-2 rounded-full flex-shrink-0",
                                                    item.type === 'log' ? 'bg-emerald-500' : 'bg-red-500'
                                                  )} />
                                                  <div className="flex-1 min-w-0">
                                                      <p className={cn("font-medium text-sm truncate", isDark ? "text-white" : "text-[#1A1A1A]")}>{item.title}</p>
                                                      
                                                      {item.type === 'log' && isCountType ? (
                                                          <div className="flex items-center gap-1.5 mt-2">
                                                              <Button
                                                                  variant="outline"
                                                                  size="icon"
                                                                  className="h-6 w-6 rounded-full border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                                                                  onClick={() => handleUpdateCount(item.id, dateStr, item.value - 1)}
                                                              >
                                                                  <Minus className="w-3 h-3" />
                                                              </Button>
                                                              <div className="w-8 h-6 flex items-center justify-center bg-blue-500/20 rounded text-sm font-bold text-blue-400">
                                                                  {item.value}
                                                              </div>
                                                              <Button
                                                                  variant="outline"
                                                                  size="icon"
                                                                  className="h-6 w-6 rounded-full border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                                                                  onClick={() => handleUpdateCount(item.id, dateStr, item.value + 1)}
                                                              >
                                                                  <Plus className="w-3 h-3" />
                                                              </Button>
                                                          </div>
                                                      ) : (
                                                          <span className={cn(
                                                            "inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-bold uppercase mt-1",
                                                            item.type === 'log' 
                                                              ? 'bg-emerald-500/20 text-emerald-400' 
                                                              : 'bg-red-500/20 text-red-400'
                                                          )}>
                                                              {item.type === 'log' ? (
                                                                <><Zap className="w-2.5 h-2.5" /> +1</>
                                                              ) : `Ended: ${item.status}`}
                                                          </span>
                                                      )}
                                                  </div>
                                              </div>
                                          </div>
                                      );
                                  })}
                              </div>
                          </ScrollArea>
                      )}
                  </CardContent>
              </Card>

              {/* Shared Goals Panel */}
              {sharedGoals.length > 0 && (
                <Card className={cn(
                  isDark ? "glass-card" : "bg-white border-[#EEEAE0] shadow-sm",
                  "mt-3",
                  !showSharedGoals && "lg:block hidden"
                )}>
                  <button
                    onClick={() => setShowSharedGoals(!showSharedGoals)}
                    className={cn(
                      "w-full flex items-center justify-between px-3 py-2 transition-colors",
                      isDark ? "hover:bg-zinc-800" : "hover:bg-[#F4F3EE]"
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-violet-400" />
                      <span className={cn("text-sm font-medium", isDark ? "text-white" : "text-[#1A1A1A]")}>Shared Goals</span>
                      <Badge variant="outline" className={cn("text-[10px]", isDark ? "border-zinc-700 text-zinc-400" : "border-[#E5E2DA] text-[#5F5F5F]")}>{sharedGoals.length}</Badge>
                    </div>
                    <ChevronDown className={cn("w-4 h-4 transition-transform", isDark ? "text-zinc-400" : "text-[#9E8778]", !showSharedGoals && "-rotate-90")} />
                  </button>
                  
                  {showSharedGoals && (
                    <CardContent className="p-3 pt-0">
                      <ScrollArea className="h-[150px]">
                        <div className="space-y-2">
                          {sharedGoals.map(goal => (
                            <div 
                              key={goal.id}
                              onClick={() => navigate(`/shared-goals/${goal.id}`)}
                              className={cn(
                                "p-2 rounded-lg border cursor-pointer transition-colors",
                                isDark ? "border-violet-500/30 bg-[#F4F3EE]0/5 hover:bg-[#F4F3EE]0/10" : "border-[#E5E2DA] bg-[#F4F3EE]/50 hover:bg-[#F4F3EE]/50"
                              )}
                            >
                              <div className="flex items-center justify-between mb-1">
                                <p className={cn("text-xs font-medium truncate", isDark ? "text-white" : "text-[#1A1A1A]")}>{goal.title}</p>
                                <div className={cn("flex items-center gap-1 text-[10px]", isDark ? "text-zinc-400" : "text-[#5F5F5F]")}>
                                  <Users className="w-3 h-3" />
                                  {goal.participant_count}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Progress 
                                  value={((goal.user_progress || 0) / goal.target_value) * 100} 
                                  className="flex-1 h-1" 
                                />
                                <span className={cn("text-[10px]", isDark ? "text-zinc-400" : "text-[#5F5F5F]")}>
                                  {goal.user_progress || 0}/{goal.target_value}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </CardContent>
                  )}
                </Card>
              )}
              </div>
          </div>
      </div>
    </div>
  );
}
